import fs from "node:fs/promises";
import path from "node:path";
import { BackupOptions } from "./types.js";

export function buildBackupPath(filePath: string, now: Date = new Date()): string {
  const stamp = now.toISOString().replace(/[:.]/g, "-");
  return `${filePath}.bak.${stamp}`;
}

export async function createBackup(filePath: string): Promise<string> {
  const backupPath = buildBackupPath(filePath);
  await fs.copyFile(filePath, backupPath);
  return backupPath;
}

function escapeRelativePath(relativePath: string): string {
  return relativePath.replace(/[\\/]/g, "__");
}

export async function createManagedBackup(
  filePath: string,
  { cwd, backupRoot, maxPerFile = 20 }: BackupOptions
): Promise<string> {
  const relativePath = path.relative(cwd, filePath);
  const stamp = new Date().toISOString().replace(/[:.]/g, "-");
  const backupName = `${escapeRelativePath(relativePath)}.${stamp}.bak`;
  const absoluteBackupRoot = path.join(cwd, backupRoot);
  const backupPath = path.join(absoluteBackupRoot, backupName);

  await fs.mkdir(absoluteBackupRoot, { recursive: true });
  await fs.copyFile(filePath, backupPath);

  const prefix = `${escapeRelativePath(relativePath)}.`;
  const entries = await fs.readdir(absoluteBackupRoot);
  const matching = entries
    .filter((entry) => entry.startsWith(prefix) && entry.endsWith(".bak"))
    .sort();

  if (matching.length > maxPerFile) {
    const excess = matching.slice(0, matching.length - maxPerFile);
    await Promise.all(excess.map((entry) => fs.unlink(path.join(absoluteBackupRoot, entry))));
  }

  return backupPath;
}

export async function createManagedPathBackup(
  targetPath: string,
  { cwd, backupRoot, maxPerFile = 20 }: BackupOptions
): Promise<string> {
  const relativePath = path.relative(cwd, targetPath);
  const stamp = new Date().toISOString().replace(/[:.]/g, "-");
  const backupName = `${escapeRelativePath(relativePath)}.${stamp}.bak`;
  const absoluteBackupRoot = path.join(cwd, backupRoot);
  const backupPath = path.join(absoluteBackupRoot, backupName);

  await fs.mkdir(absoluteBackupRoot, { recursive: true });
  await fs.rename(targetPath, backupPath);

  const prefix = `${escapeRelativePath(relativePath)}.`;
  const entries = await fs.readdir(absoluteBackupRoot);
  const matching = entries
    .filter((entry) => entry.startsWith(prefix) && entry.endsWith(".bak"))
    .sort();

  if (matching.length > maxPerFile) {
    const excess = matching.slice(0, matching.length - maxPerFile);
    await Promise.all(excess.map((entry) => fs.rm(path.join(absoluteBackupRoot, entry), { recursive: true, force: true })));
  }

  return backupPath;
}
